import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Save, History, RotateCcw, Eye, Trash2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import QuestionEditor, { QB_STYLES } from "@/components/admin/questionnaire/QuestionEditor";
import { QuestionField, isVisible, SR_STYLES } from "@/components/servicerequest/QuestionField";

let uid = 0;
const k = () => `k${++uid}`;
const hydrate = (sections) => (sections || []).map((s) => ({ ...s, _k: k(), questions: s.questions.map((q) => ({ ...q, _k: k() })) }));
const strip = (sections) => sections.map((s) => ({ title: s.title, questions: s.questions.map(({ _k, _new, ...q }) => ({ ...q, options: (q.options || []).map((o) => o.trim()).filter(Boolean) })) }));
const move = (arr, i, d) => { const j = i + d; if (j < 0 || j >= arr.length) return arr; const a = [...arr]; [a[i], a[j]] = [a[j], a[i]]; return a; };

function Preview({ sections }) {
  const [answers, setAnswers] = useState({});
  return (
    <div className="rounded-2xl border border-slate-200 bg-[#f8fafb] p-5 space-y-6" data-testid="qb-preview">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#165a71]"><Eye className="h-3.5 w-3.5" /> Customer preview</div>
      {sections.map((s) => (
        <div key={s._k} className="space-y-5">
          <div className="font-display font-semibold text-[#071b22]">{s.title || "Untitled section"}</div>
          {s.questions.filter((q) => isVisible(q, answers)).map((q) => <QuestionField key={q._k} q={{ ...q, options: q.type === "yesno" ? ["Yes", "No"] : (q.options || []).filter(Boolean) }} value={answers[q.id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} />)}
        </div>
      ))}
    </div>
  );
}

export default function AdminQuestionnaireEditorPage() {
  const { key } = useParams();
  const { user } = useAuth();
  const canEdit = (user?.roles || []).some((r) => ["administrator", "super_administrator"].includes(r));
  const [d, setD] = useState(null);
  const [sections, setSections] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(() => api.get(`/admin/service-requests/questionnaires/${key}`).then((r) => { setD(r.data); setSections(hydrate(r.data.current?.sections)); setViewing(null); }), [key]);
  useEffect(() => { load(); }, [load]);
  const allQs = useMemo(() => sections.flatMap((s) => s.questions), [sections]);

  if (!d) return <div className="text-[#252A34]/60 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  const ro = !canEdit || !!viewing;
  const shown = viewing ? hydrate(viewing.sections) : sections;
  const setSec = (i, s) => setSections((all) => all.map((x, j) => (j === i ? s : x)));
  const addQ = (i) => { const id = `question_${allQs.length + 1}`; setSec(i, { ...sections[i], questions: [...sections[i].questions, { _k: k(), _new: true, id, label: "", type: "single", options: ["Option A", "Option B"], required: false, show_if: null }] }); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/service-requests/questionnaires", { category_key: key, sections: strip(sections), note: note || null });
      toast.success(`Published version ${data.version} — live for new requests`);
      setNote("");
      load();
    } catch (err) { toast.error(err?.response?.data?.error || "Could not save"); } finally { setSaving(false); }
  };
  const restore = async (v) => {
    if (!window.confirm(`Restore version ${v.version}? It will be published as a new version.`)) return;
    try { const { data } = await api.post(`/admin/service-requests/questionnaires/${key}/versions/${v.id}/restore`); toast.success(`Restored as v${data.version}`); load(); }
    catch (err) { toast.error(err?.response?.data?.error || "Restore failed"); }
  };
  const view = async (v) => { const { data } = await api.get(`/admin/service-requests/questionnaires/${key}/versions/${v.id}`); setViewing(data); };
  const removeCustom = async () => {
    if (!window.confirm("Stop using custom questions for this category? New requests will use the general questionnaire. History is kept.")) return;
    await api.delete(`/admin/service-requests/questionnaires/${key}`); toast.success("Now using the general questionnaire"); load();
  };

  return (
    <div className="max-w-7xl" data-testid="admin-questionnaire-editor">
      <style>{QB_STYLES + SR_STYLES}</style>
      <Link to="/admin/questionnaires" className="text-sm text-[#165a71]">← Questionnaires</Link>
      <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold text-[#071b22]" data-testid="qb-title">{d.category?.name || "General questionnaire"}</h1>
          <div className="text-sm text-[#252A34]/60 mt-1" data-testid="qb-version">
            {d.current?.from_generic ? "No custom questions yet — showing the questions it currently inherits" : `Live: version ${d.current.version}`}
            {viewing && <span className="ml-2 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs">Viewing v{viewing.version} (read-only)</span>}
          </div>
        </div>
        {canEdit && !viewing && (
          <div className="flex items-center gap-2 flex-wrap">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changed? (optional)" className="qb-input w-56" data-testid="qb-note" />
            <button onClick={save} disabled={saving} className="rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2 hover:bg-[#11485a] disabled:opacity-60" data-testid="qb-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save & publish</button>
          </div>
        )}
        {viewing && <button onClick={() => setViewing(null)} className="rounded-full border border-slate-200 px-4 py-2 text-sm" data-testid="qb-back-to-live">Back to live version</button>}
      </div>
      {!canEdit && <div className="mt-3 rounded-xl bg-slate-100 px-4 py-2 text-xs text-[#252A34]/70" data-testid="qb-readonly-note">View only — administrators can edit questionnaires.</div>}

      <div className="mt-6 grid gap-6 xl:grid-cols-[1.2fr_1fr_260px] items-start">
        <div className="space-y-5">
          {shown.map((s, i) => (
            <div key={s._k} className="rounded-3xl border border-slate-200 bg-slate-50/60 p-4 space-y-3" data-testid={`qb-section-${i}`}>
              <div className="flex items-center gap-2">
                <input value={s.title} disabled={ro} onChange={(e) => setSec(i, { ...s, title: e.target.value })} placeholder="Section title" className="qb-input font-display font-semibold" data-testid={`qb-section-${i}-title`} />
                {!ro && <button onClick={() => setSections(move(sections, i, -1))} className="qb-icon" title="Move up" data-testid={`qb-section-${i}-up`}>↑</button>}
                {!ro && <button onClick={() => setSections(move(sections, i, 1))} className="qb-icon" title="Move down" data-testid={`qb-section-${i}-down`}>↓</button>}
                {!ro && <button onClick={() => window.confirm("Delete this section?") && setSections(sections.filter((_, j) => j !== i))} className="qb-icon text-rose-600" data-testid={`qb-section-${i}-remove`}><Trash2 className="h-3.5 w-3.5" /></button>}
              </div>
              {s.questions.map((q, qi) => {
                const before = [...shown.slice(0, i).flatMap((x) => x.questions), ...s.questions.slice(0, qi)];
                return <QuestionEditor key={q._k} q={q} earlier={before} readOnly={ro} tid={`qb-q-${i}-${qi}`}
                  onChange={(nq) => setSec(i, { ...s, questions: s.questions.map((x, j) => (j === qi ? nq : x)) })}
                  onMove={(dir) => setSec(i, { ...s, questions: move(s.questions, qi, dir) })}
                  onRemove={() => setSec(i, { ...s, questions: s.questions.filter((_, j) => j !== qi) })} />;
              })}
              {!ro && <button onClick={() => addQ(i)} className="w-full rounded-2xl border-2 border-dashed border-slate-300 py-3 text-sm text-[#165a71] inline-flex items-center justify-center gap-2 hover:bg-white" data-testid={`qb-section-${i}-add-question`}><Plus className="h-4 w-4" /> Add question</button>}
            </div>
          ))}
          {!ro && <button onClick={() => setSections([...sections, { _k: k(), title: "New section", questions: [] }])} className="rounded-full border border-[#165a71]/40 text-[#165a71] px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="qb-add-section"><Plus className="h-4 w-4" /> Add section</button>}
        </div>
        <div className="xl:sticky xl:top-6"><Preview sections={shown} /></div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="qb-history">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#252A34]/60"><History className="h-3.5 w-3.5" /> Version history</div>
          <div className="mt-3 space-y-2">
            {d.versions.map((v) => (
              <div key={v.id} className="rounded-xl border border-slate-100 p-3 text-xs" data-testid={`qb-version-${v.version}`}>
                <div className="flex items-center justify-between"><span className="font-semibold text-[#071b22]">v{v.version}</span><span className={`rounded-full px-2 py-0.5 ${v.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-500"}`}>{v.status === "published" ? "live" : "archived"}</span></div>
                <div className="text-[#252A34]/60 mt-1">{v.updated_at ? new Date(v.updated_at).toLocaleString() : ""}{v.saved_by ? ` · ${v.saved_by}` : ""}</div>
                {v.note && <div className="mt-1 text-[#252A34]/80">{v.note}</div>}
                <div className="mt-2 flex gap-3">
                  <button onClick={() => view(v)} className="text-[#165a71] underline" data-testid={`qb-view-${v.version}`}>View</button>
                  {canEdit && v.status !== "published" && <button onClick={() => restore(v)} className="text-[#165a71] underline inline-flex items-center gap-1" data-testid={`qb-restore-${v.version}`}><RotateCcw className="h-3 w-3" />Restore</button>}
                </div>
              </div>
            ))}
            {!d.versions.length && <div className="text-xs text-[#252A34]/60">No versions yet.</div>}
          </div>
          {canEdit && key !== "_generic" && !d.current?.from_generic && <button onClick={removeCustom} className="mt-4 text-xs text-rose-700 underline" data-testid="qb-remove-custom">Use general questionnaire instead</button>}
        </div>
      </div>
    </div>
  );
}
